
import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Brain, Send } from "lucide-react";
import { analyzeWithAI } from "@/services/aiAnalysis";

export function AIConsultation({ examValues, analysisResults, calculatorType, gender }) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Análise inicial automática
  useEffect(() => {
    if (examValues && Object.keys(examValues).length > 0) {
      performInitialAnalysis();
    }
  }, [examValues, analysisResults]);

  const performInitialAnalysis = async () => {
    try {
      const response = await analyzeWithAI({
        examValues,
        analysisResults,
        calculatorType,
        gender
      });

      setMessages([{
        role: "assistant",
        content: response.content
      }]);
    } catch (error) {
      toast({
        title: "Erro na análise",
        description: "Não foi possível analisar os resultados",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = { role: "user", content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const response = await analyzeWithAI({
        examValues,
        analysisResults,
        calculatorType,
        gender,
        userQuery: input
      });

      setMessages(prev => [...prev, {
        role: "assistant",
        content: response.content
      }]);
    } catch (error) {
      toast({
        title: "Erro na consulta",
        description: "Não foi possível obter resposta",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="h-96 overflow-y-auto p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`p-3 rounded-lg mb-4 ${
              msg.role === "user"
                ? "bg-[#ff3366]/10 ml-8"
                : "bg-[#1a1a2e] mr-8"
            }`}
          >
            <pre className="whitespace-pre-wrap font-sans text-sm text-white/90">
              {msg.content}
            </pre>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Faça uma pergunta sobre seus resultados..."
          className="flex-1 bg-[#16213e] border-[#ff3366]/20 text-white"
          disabled={isLoading}
        />
        <Button 
          type="submit"
          className="bg-[#ff3366] hover:bg-[#ff3366]/80"
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </form>
    </div>
  );
}
